import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function AuthErrorHandler() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    // Check if user just signed in with invitation metadata
    const checkForInvitationMetadata = async () => {
      if (
        user?.user_metadata?.invitationToken &&
        user?.user_metadata?.organizationId
      ) {
        console.log(
          "[AuthErrorHandler] User has invitation metadata, redirecting to accept invite",
        );
        const token = user.user_metadata.invitationToken;
        const orgId = user.user_metadata.organizationId;

        // Clear the metadata to prevent repeated redirects
        await supabase.auth.updateUser({
          data: {
            invitationToken: null,
            organizationId: null,
            organizationName: null,
            inviterName: null,
          },
        });

        // Redirect to the invite acceptance page
        navigate(`/invite/${token}?org=${orgId}`);
        return;
      }
    };

    checkForInvitationMetadata();
  }, [user, navigate]);

  useEffect(() => {
    // Check for auth errors in the URL hash
    if (typeof window !== "undefined" && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));

      const error = hashParams.get("error");
      const errorCode = hashParams.get("error_code");
      const errorDescription = hashParams.get("error_description");

      if (error || errorCode) {
        console.log("[AuthErrorHandler] Auth error detected:", {
          error,
          errorCode,
          errorDescription,
        });

        // Clear the hash to prevent infinite loops
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );

        // Handle specific error codes
        if (errorCode === "otp_expired") {
          toast({
            variant: "destructive",
            title: "Invitation link expired",
            description:
              "This invitation link has expired. Please request a new invitation from your team administrator.",
          });

          // Check if this was an invite link by looking at the URL or stored data
          const storedRedirect = sessionStorage.getItem("authRedirect");
          if (
            storedRedirect?.includes("/accept-invite") ||
            storedRedirect?.includes("/invite/")
          ) {
            // This was likely an invite link, redirect to login
            navigate("/login");
          } else {
            // For other cases, redirect to login
            navigate("/login");
          }
        } else if (error === "access_denied") {
          toast({
            variant: "destructive",
            title: "Access denied",
            description:
              errorDescription ||
              "You don't have permission to access this resource.",
          });
          navigate("/login");
        } else if (errorCode === "invalid_request") {
          toast({
            variant: "destructive",
            title: "Invalid request",
            description:
              errorDescription ||
              "The authentication request was invalid. Please try again.",
          });
          navigate("/login");
        } else {
          // Generic error handling
          toast({
            variant: "destructive",
            title: "Authentication error",
            description:
              errorDescription ||
              "An error occurred during authentication. Please try again.",
          });
          navigate("/login");
        }
      }
    }
  }, [navigate, toast]);

  return null;
}
